import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Package,
  Loader2,
  Calendar,
  Building2,
  AlertTriangle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { formatDate, formatCurrency, sumCantidad, sumSubtotal } from './format';

/**
 * Dialog fullscreen para revisar/editar los precios de una entrega
 * dentro del wizard de creación de liquidación.
 *
 * Props:
 *  - entrega: la entrega a mostrar (null si está cerrado)
 *  - onClose: callback al cerrar
 *  - onPrecioChange(entregaId, prendaId, nuevoPrecio)  -> debe devolver Promise
 *  - savingKey: clave "entregaId::prendaId" que está guardando (para mostrar loader)
 */
const LiquidacionPreviewDetailDialog = ({ entrega, onClose, onPrecioChange, savingKey }) => {
  // Si no hay entrega, no renderizamos nada. El Dialog de shadcn ya tiene
  // su propia animación de salida (data-state="closed").
  if (!entrega) return null;

  const items = entrega.items || [];
  const totalCantidad = sumCantidad(items);
  const subtotal = sumSubtotal(items);

  return (
    <Dialog open={!!entrega} onOpenChange={onClose}>
      <DialogContent
        className="!fixed !inset-0 !left-0 !top-0 !translate-x-0 !translate-y-0 !max-w-none !w-full !h-full !rounded-none !p-0 !gap-0 !border-0 flex flex-col [&>button]:hidden bg-white"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={onClose}
      >
        <DialogTitle className="sr-only">
          Ajustar precios de la entrega {entrega.consecutivo}
        </DialogTitle>
        <AnimatePresence>
          {entrega && (
            <motion.div
              key="liq-detail"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="flex flex-col h-full"
            >
              {/* Header */}
              <div className="bg-emerald-600 text-white p-4 flex items-center justify-between shrink-0">
                <div>
                  <h2 className="text-lg font-bold flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/20 text-xs font-bold">
                      E
                    </span>
                    {entrega.consecutivo}
                  </h2>
                  <p className="text-emerald-100 text-sm">
                    {entrega.lavanderia?.nombre || '—'}
                  </p>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-white hover:bg-emerald-700 h-8 w-8"
                  onClick={onClose}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {/* Badges / info general */}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700">Entrega</Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Fecha entrega
                    </p>
                    <p className="text-sm font-medium">{formatDate(entrega.fechaEntrega)}</p>
                  </div>
                  {entrega.lavanderia?.nombre && (
                    <div className="bg-white rounded-lg p-3 border">
                      <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <Building2 className="h-3.5 w-3.5" /> Lavandería
                      </p>
                      <p className="text-sm font-medium">{entrega.lavanderia.nombre}</p>
                    </div>
                  )}
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-gray-500 mb-1">Prendas</p>
                    <p className="text-2xl font-bold text-emerald-700">{totalCantidad}</p>
                  </div>
                </div>

                {/* Items editables */}
                <div className="bg-white rounded-lg border">
                  <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-semibold text-sm text-gray-700">
                      Detalle de prendas ({items.length})
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {entrega.tipoEntrega && entrega.tipoEntrega !== 'ordinaria' && (
                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 font-semibold">
                          Recargo {entrega.tipoEntrega}
                        </span>
                      )}
                      <span>Ajustá los precios según corresponda</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-500">
                        <tr>
                          <th className="text-left py-2 px-3 font-medium">Prenda</th>
                          <th className="text-right py-2 px-3 font-medium">Cantidad</th>
                          <th className="text-right py-2 px-3 font-medium">Precio base</th>
                          <th className="text-right py-2 px-3 font-medium">% Apl.</th>
                          <th className="text-right py-2 px-3 font-medium w-44">
                            Precio final
                          </th>
                          <th className="text-right py-2 px-3 font-medium">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it) => {
                          const key = `${entrega.entregaId}::${it.prendaId}`;
                          const isSaving = savingKey === key;
                          const hayRecargo = it.porcentaje != null && it.porcentaje !== 100;
                          const base = it.precioBase != null ? Number(it.precioBase) : Number(it.precioUnitario);
                          const pct = it.porcentaje != null ? Number(it.porcentaje) : 100;
                          return (
                            <tr key={key} className="border-t border-gray-100">
                              <td className="py-2 px-3">
                                <div className="flex items-center gap-2">
                                  <Package className="h-3.5 w-3.5 text-gray-400" />
                                  <span className="font-medium">{it.nombre}</span>
                                </div>
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums">
                                {it.cantidad}
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums text-gray-500">
                                {formatCurrency(base)}
                                {hayRecargo && (
                                  <div className="text-[10px] text-amber-600">
                                    recargo: {formatCurrency(it.recargo || 0)}
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right">
                                {hayRecargo ? (
                                  <span className="inline-flex items-center justify-center bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums">
                                    {pct}%
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">100%</span>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Input
                                    type="number"
                                    min={0}
                                    step="any"
                                    className="h-8 w-32 text-right tabular-nums"
                                    value={it.precioUnitario}
                                    onChange={(e) =>
                                      onPrecioChange?.(
                                        entrega.entregaId,
                                        it.prendaId,
                                        e.target.value,
                                      )
                                    }
                                    disabled={isSaving}
                                  />
                                  {isSaving && (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                                  )}
                                </div>
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums font-semibold text-emerald-700">
                                {formatCurrency(it.subtotal)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Advertencia */}
                <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-lg p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Los precios modificados se guardan automáticamente al cambiar el valor.
                    Quedarán como snapshot en la liquidación una vez creada.
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t bg-white flex items-center justify-between shrink-0">
                <div>
                  <p className="text-xs text-gray-500">Subtotal de la entrega</p>
                  <p className="text-2xl font-bold text-emerald-700">
                    {formatCurrency(subtotal)}
                  </p>
                </div>
                <Button onClick={onClose} className="bg-gray-900 hover:bg-gray-700">
                  Listo
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default LiquidacionPreviewDetailDialog;
